import { ApiError, getConsoleConnections, type ConsoleConnections } from "./api";
export type ChatConnection = Pick<ConsoleConnections, "controlBase" | "controlToken">;

export type RuntimeKind = "claude" | "codex" | "deepseek_harness" | "opencode" | "cursor";
export type SessionStatus =
  | "creating"
  | "idle"
  | "running"
  | "dormant"
  | "unavailable"
  | "archived";
export type TurnStatus =
  | "queued"
  | "running"
  | "succeeded"
  | "failed"
  | "cancelled"
  | "unknown";

export interface ChatCatalog {
  agents: Array<{ name: string; runtime_kind: RuntimeKind; has_hil_policy?: boolean }>;
  workers: Array<{
    name: string;
    kind: "local" | "ssh" | "reverse";
    max_concurrency: number;
    available_slots: number;
    online?: boolean;
  }>;
}

export interface ChatSession {
  id: string;
  agent_name: string;
  runtime_kind: RuntimeKind;
  worker_name: string;
  working_directory?: string | null;
  source_directory?: string | null;
  adapter_name: string | null;
  adapter_version: string | null;
  protocol_version: number | null;
  status: SessionStatus;
  active_turn_id: string | null;
  title: string | null;
  pinned?: boolean;
  review_required?: boolean;
  pending_review_turn_id?: string | null;
  last_turn_status?: TurnStatus | null;
  created_at: string;
  updated_at: string;
  last_activity_at: string;
}

export interface ChatTurn {
  id: string;
  session_id: string;
  client_message_id: string;
  status: TurnStatus;
  user_message: unknown;
  final_assistant: string | null;
  usage: unknown | null;
  error: string | null;
  created_at: string;
  started_at: string | null;
  ended_at: string | null;
}

export interface ChatSessionDetail {
  session: ChatSession;
  turns: ChatTurn[];
}

export type ChatEvent =
  | { type: "subagent_spawned"; id: string; parent_id: string | null; name: string; task: string }
  | { type: "subagent_state"; id: string; state: string }
  | { type: "subagent_event"; id: string; event: ChatEvent }
  | { type: "user_message"; text: string }
  | { type: "compaction"; id: string; status: string }
  | { type: "session_ready" }
  | { type: "turn_started" }
  | { type: "assistant_delta"; text: string }
  | {
      type: "tool_call";
      tool_call_id: string;
      title?: string | null;
      status?: string | null;
      kind?: string | null;
      content?: unknown;
      locations?: unknown;
      raw_input?: unknown;
      raw_output?: unknown;
      terminal?: { output_delta?: string; working_directory?: string; exit_code?: number; exit_signal?: string };
    }
  | { type: "plan_updated"; plan: unknown }
  | { type: "usage_updated"; usage: unknown }
  | { type: "provider_notice"; code: string; message: string }
  | { type: "turn_completed"; stop_reason: string }
  | { type: "turn_failed"; error: string }
  | { type: "turn_cancelled" }
  | { type: "adapter_restarted" };

export interface ChatEventRecord {
  id: number;
  session_id: string;
  turn_id: string | null;
  seq: number;
  event: ChatEvent;
  created_at: string;
}

export function getChatCatalog() {
  return controlRequest<ChatCatalog>("/v1/chat/catalog");
}

export interface RuntimeReadiness {
  runtime_kind: RuntimeKind;
  checked_at: string;
  checks: Array<{ id: string; status: "pass" | "error" | "unknown"; message: string }>;
}
export function checkCodexReadiness(input: { agent_name: string; worker_name: string; working_directory?: string; isolated_worktree?: boolean }, signal?: AbortSignal) {
  return controlRequest<RuntimeReadiness>("/v1/chat/preflight", { method: "POST", body: JSON.stringify(input), signal });
}

export function listChatSessions(options: { archived?: boolean; search?: string; projectId?: string; offset?: number; peerOf?: string; afterId?: string; stable?: boolean } = {}, connection?: ChatConnection, signal?: AbortSignal) {
  const query = new URLSearchParams({ limit: "200" });
  if (options.stable) query.set("stable", "true");
  if (options.afterId) query.set("after_id", options.afterId);
  if (options.projectId) query.set("project_id", options.projectId);
  if (options.offset) query.set("offset", String(options.offset));
  if (options.peerOf) query.set("peer_of", options.peerOf);
  if (options.archived) query.set("archived", "true");
  if (options.search) query.set("q", options.search);
  return controlRequest<ChatSession[]>("/v1/chat/sessions?" + query, { signal }, connection);
}

/** Fetch every page rather than silently hiding running/review sessions after 200.
 * Metadata only; react-query retains the previous successful sweep on errors. */
export async function listAllChatSessions(options: { archived?: boolean; search?: string; projectId?: string } = {}, connection: ChatConnection = getConsoleConnections(), signal?: AbortSignal) {
  const sessions = new Map<string, ChatSession>();
  let afterId = "";
  for (;;) {
    const page = await listChatSessions({ ...options, afterId, stable: true }, connection, signal);
    const before = sessions.size;
    for (const session of page) sessions.set(session.id, session);
    if (page.length === 200 && sessions.size === before) throw new Error("Session pagination made no progress. Update the control server and reload.");
    if (page.length < 200) return Array.from(sessions.values()).sort((a, b) => Number(!!b.pinned) - Number(!!a.pinned) || b.last_activity_at.localeCompare(a.last_activity_at) || b.id.localeCompare(a.id));
    afterId = page.at(-1)!.id;
  }
}
export interface SessionChangesPage {
  epoch: string; cursor: string; through: string; reset: boolean; has_more: boolean;
  changes: Array<{ id: string; session: ChatSession | null }>;
}
export function getSessionChanges(cursor: { epoch?: string; after: string; through?: string }, connection: ChatConnection, signal?: AbortSignal) {
  const query = new URLSearchParams({ after: cursor.after, limit: "200" });
  if (cursor.epoch !== undefined) query.set("epoch", cursor.epoch);
  if (cursor.through !== undefined) query.set("through", cursor.through);
  return controlRequest<SessionChangesPage>("/v1/chat/session-changes?" + query, { signal }, connection);
}

export function updateChatSession(id: string, patch: { title?: string; pinned?: boolean }) {
  return controlRequest<ChatSession>("/v1/chat/sessions/" + encodeURIComponent(id), {
    method: "PATCH", body: JSON.stringify(patch),
  });
}

export function getChatSession(id: string) {
  return controlRequest<ChatSessionDetail>(
    `/v1/chat/sessions/${encodeURIComponent(id)}`,
  );
}

export function createChatSession(input: {
  agent_name: string;
  worker_name: string;
  title?: string;
  working_directory?: string;
  isolated_worktree?: boolean;
  review_required?: boolean;
}, connection?: ChatConnection) {
  return controlRequest<ChatSession>("/v1/chat/sessions", {
    method: "POST",
    body: JSON.stringify(input),
  }, connection);
}

export function sendChatMessage(
  sessionId: string,
  input: { client_message_id: string; message: string | RichMessage },
  connection?: ChatConnection,
) {
  return controlRequest<{ turn: ChatTurn; created: boolean }>(
    `/v1/chat/sessions/${encodeURIComponent(sessionId)}/messages`,
    { method: "POST", body: JSON.stringify(input) },
    connection,
  );
}

export async function cancelChatTurn(sessionId: string) {
  await controlRequest<void>(
    `/v1/chat/sessions/${encodeURIComponent(sessionId)}/cancel`,
    { method: "POST" },
  );
}

export async function archiveChatSession(sessionId: string) {
  await controlRequest<void>(
    `/v1/chat/sessions/${encodeURIComponent(sessionId)}`,
    { method: "DELETE" },
  );
}

/**
 * Consume one authenticated SSE connection and return the highest persisted
 * sequence observed. Reconnection policy stays with the caller; framing,
 * bearer headers, cursors, and abort behavior stay hidden here.
 */
export async function streamChatEvents(
  sessionId: string,
  afterSeq: number,
  signal: AbortSignal,
  onEvent: (event: ChatEventRecord) => void,
  onConnected?: () => void,
): Promise<number> {
  const response = await controlFetch(
    `/v1/chat/sessions/${encodeURIComponent(sessionId)}/events?after_seq=${afterSeq}`,
    {
      signal,
      headers: {
        Accept: "text/event-stream",
        "Last-Event-ID": String(afterSeq),
      },
    },
  );
  if (!response.ok) throw await responseError(response);
  if (!response.body) throw new ApiError(0, "Control API returned no event stream.");

  onConnected?.();
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let cursor = afterSeq;
  while (true) {
    const { done, value } = await reader.read();
    buffer += decoder.decode(value, { stream: !done });
    buffer = buffer.replace(/\r\n/g, "\n");
    let boundary = buffer.indexOf("\n\n");
    while (boundary >= 0) {
      const frame = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);
      const parsed = parseSseFrame(frame);
      if (parsed.data) {
        if (parsed.event === "stream_lagged" || parsed.event === "stream_error") {
          throw new ApiError(0, `Event stream interrupted: ${parsed.data}`);
        }
        const record = JSON.parse(parsed.data) as ChatEventRecord;
        cursor = Math.max(cursor, record.seq, parsed.id ?? 0);
        onEvent(record);
      }
      boundary = buffer.indexOf("\n\n");
    }
    if (done) return cursor;
  }
}

async function controlRequest<T>(path: string, init: RequestInit = {}, connection?: ChatConnection): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body) headers.set("Content-Type", "application/json");
  const response = await controlFetch(path, { ...init, headers }, connection);
  if (!response.ok) throw await responseError(response);
  if (response.status === 204 || response.headers.get("content-length") === "0") {
    return undefined as T;
  }
  return (await response.json()) as T;
}

async function controlFetch(path: string, init: RequestInit, connection?: ChatConnection): Promise<Response> {
  const { controlBase, controlToken } = connection ?? requireControlConnection();
  if (!controlToken) throw new ApiError(401, "Control API token required.");
  const endpoint = `${controlBase}${path}`;
  const headers = new Headers(init.headers);
  headers.set("Authorization", `Bearer ${controlToken}`);
  try {
    return await fetch(endpoint, { ...init, headers });
  } catch (error) {
    if (init.signal?.aborted || (error instanceof DOMException && error.name === "AbortError")) {
      throw error;
    }
    throw new ApiError(0, `Could not reach control API at ${safeControlEndpoint(endpoint)}`);
  }
}

function safeControlEndpoint(endpoint: string): string {
  try {
    const base = typeof window === "undefined" ? "http://localhost/" : window.location.href;
    const url = new URL(endpoint, base);
    url.username = "";
    url.password = "";
    url.hash = "";
    return url.toString();
  } catch {
    return "the configured control endpoint";
  }
}

function requireControlConnection() {
  const { controlBase, controlToken } = getConsoleConnections();
  if (!controlBase) throw new ApiError(0, "Control API is not configured.");
  if (!controlToken) throw new ApiError(401, "Control bearer token required.");
  return { controlBase, controlToken };
}

async function responseError(response: Response): Promise<ApiError> {
  const body = await response.text().catch(() => "");
  let message = body || response.statusText;
  try {
    const decoded = JSON.parse(body) as { error?: string };
    if (decoded.error) message = decoded.error;
  } catch {
    // Plain-text failures are already suitable for the operator.
  }
  return new ApiError(response.status, message);
}

function parseSseFrame(frame: string): {
  id: number | null;
  event: string;
  data: string;
} {
  let id: number | null = null;
  let event = "message";
  const data: string[] = [];
  for (const line of frame.split("\n")) {
    if (!line || line.startsWith(":")) continue;
    const separator = line.indexOf(":");
    const field = separator < 0 ? line : line.slice(0, separator);
    const value = separator < 0 ? "" : line.slice(separator + 1).replace(/^ /, "");
    if (field === "id") {
      const parsed = Number(value);
      if (Number.isSafeInteger(parsed)) id = parsed;
    } else if (field === "event") {
      event = value;
    } else if (field === "data") {
      data.push(value);
    }
  }
  return { id, event, data: data.join("\n") };
}

export interface DirectoryListing {
  path: string; parent: string; entries: Array<{ name: string; path: string }>; truncated: boolean;
}
export interface FileListing {
  path: string; entries: Array<{ name: string; path: string; kind: "directory" | "file" | "symlink" | "special" }>; truncated: boolean;
}
export interface FilePreview { path: string; text: string; size: number; snapshot: boolean }
export interface GitInspection {
  available: boolean; reason?: string; branch?: string; files?: Array<{ path: string; status: string }>;
  diff?: string; truncated?: boolean; scope?: string; skipped?: string[]; end_snapshot?: boolean;
}
export function browseDirectories(worker: string, path = "") {
  return controlRequest<DirectoryListing>("/v1/chat/directories?" + new URLSearchParams({ worker, path }));
}
export function inspectWorkspace<T>(sessionId: string, action: "files" | "read" | "git" | "search", path = "") {
  return controlRequest<T>("/v1/chat/sessions/" + encodeURIComponent(sessionId) + "/workspace?" + new URLSearchParams({ action, path }));
}

export interface ProjectInput { name: string; worker_name: string; working_directory: string; agent_name: string; favorite: boolean }
export interface Project extends ProjectInput {
  last_used_at?: string | null; id: string; updated_at: string }
export function listProjects() { return controlRequest<Project[]>("/v1/chat/projects"); }
export function saveProject(input: ProjectInput, id?: string) {
  return controlRequest<Project>("/v1/chat/projects" + (id ? "/" + encodeURIComponent(id) : ""), { method: id ? "PUT" : "POST", body: JSON.stringify(input) });
}
export function deleteProject(id: string) {
  return controlRequest<void>("/v1/chat/projects/" + encodeURIComponent(id), { method: "DELETE" });
}

export function reviewTurnChanges(sessionId: string, turnId: string) {
  return controlRequest<GitInspection>("/v1/chat/sessions/" + encodeURIComponent(sessionId) + "/turns/" + encodeURIComponent(turnId) + "/changes");
}

export interface ChatImage { name: string; mime_type: string; data: string }
export interface RichMessage { swarm_message: 1; text: string; images: ChatImage[] }
export interface SessionCapabilities {
  compact?: boolean; subagents?: boolean;
  attached: boolean; resume: boolean | null; images: boolean | null; embedded_context: boolean | null; steering: boolean;
  config_options: Array<{ id: string; name: string; currentValue: string | null; options: Array<{ name: string; value: string }> }>;
}
export function getSessionCapabilities(id: string) {
  return controlRequest<SessionCapabilities>("/v1/chat/sessions/" + encodeURIComponent(id) + "/capabilities");
}
export function connectSession(id: string) {
  return controlRequest<SessionCapabilities>("/v1/chat/sessions/" + encodeURIComponent(id) + "/connect", { method: "POST" });
}
export function setSessionConfig(sessionId: string, id: string, value: string) {
  return controlRequest<SessionCapabilities>("/v1/chat/sessions/" + encodeURIComponent(sessionId) + "/capabilities", { method: "PUT", body: JSON.stringify({ id, value }) });
}

export interface QueuedMessage {
  id: string;
  client_message_id: string;
  message: string | RichMessage;
  status: "waiting" | "dispatched" | "cancelled";
  created_at: string;
}
export interface MessageQueue { paused: boolean; reason: string | null; items: QueuedMessage[] }
export function getMessageQueue(id: string) {
  return controlRequest<MessageQueue>("/v1/chat/sessions/" + encodeURIComponent(id) + "/queue");
}
export function enqueueMessage(id: string, input: { client_message_id: string; message: string | RichMessage }, connection?: ChatConnection) {
  return controlRequest<QueuedMessage>("/v1/chat/sessions/" + encodeURIComponent(id) + "/queue", { method: "POST", body: JSON.stringify(input) }, connection);
}
export function controlMessageQueue(id: string, paused: boolean) {
  return controlRequest<void>("/v1/chat/sessions/" + encodeURIComponent(id) + "/queue", { method: "PATCH", body: JSON.stringify({ paused }) });
}
export function cancelQueuedMessage(id: string, item: string) {
  return controlRequest<void>("/v1/chat/sessions/" + encodeURIComponent(id) + "/queue/" + encodeURIComponent(item), { method: "DELETE" });
}

export type ReviewDecision = { action: "approve" } | { action: "modify"; value: unknown } | { action: "reject"; value: { reason: string } };
export interface ChatReview {
  turn_id: string;
  session_id: string;
  decision: ReviewDecision | null;
  reviewed_output: unknown;
  decided_by: string | null;
  created_at: string;
  decided_at: string | null;
}
export function getChatReviews(sessionId: string) {
  return controlRequest<ChatReview[]>("/v1/chat/sessions/" + encodeURIComponent(sessionId) + "/reviews");
}
export function resolveChatReview(sessionId: string, turnId: string, decision: ReviewDecision) {
  return controlRequest<ChatReview>("/v1/chat/sessions/" + encodeURIComponent(sessionId) + "/reviews/" + encodeURIComponent(turnId), {
    method: "PUT", body: JSON.stringify(decision),
  });
}

export function getSavedSessionConfig(sessionId: string) {
  return controlRequest<Array<[string, string]>>("/v1/chat/sessions/" + encodeURIComponent(sessionId) + "/saved-config");
}
export function clearSavedSessionConfig(sessionId: string) {
  return controlRequest<void>("/v1/chat/sessions/" + encodeURIComponent(sessionId) + "/saved-config", { method: "DELETE" });
}


export type WorkspaceOperation = "commit" | "undo";
export interface WorkspaceActionRequest {
  action_id?: string;
  action: "preview" | "apply" | "reconcile" | "acknowledge";
  operation: WorkspaceOperation; paths?: string[]; fingerprint?: string; message?: string;
}
export interface WorkspaceActionReceipt {
  operation?: WorkspaceOperation; paths?: string[]; fingerprint?: string; head?: string;
  branch?: string; backup?: string; message?: string | null;
  result?: { status: "succeeded" | "unknown" | "not_started"; message?: string; commit?: string; branch?: string; backup?: string; paths?: string[] };
  operator_acknowledged?: boolean; receipt?: WorkspaceActionReceipt;
}
export interface WorkspaceActionState {
  action_id: string; turn_id: string; operation: WorkspaceOperation;
  status: "running" | "unknown" | "resolved"; result: WorkspaceActionReceipt | null;
}
export function getWorkspaceAction(id: string) {
  return controlRequest<WorkspaceActionState | null>("/v1/chat/sessions/" + encodeURIComponent(id) + "/workspace-action");
}
export function workspaceAction(id: string, turn: string, input: WorkspaceActionRequest) {
  return controlRequest<WorkspaceActionReceipt>("/v1/chat/sessions/" + encodeURIComponent(id) + "/turns/" + encodeURIComponent(turn) + "/workspace-action", { method: "POST", body: JSON.stringify(input) });
}

export interface AgentConfiguration { name: string; revision: string; document: string }
export function getAgentConfiguration(name: string) {
  return controlRequest<AgentConfiguration>('/v1/chat/agents/' + encodeURIComponent(name) + '/config');
}
export function publishAgentConfiguration(name: string, document: string, revision: string | null) {
  return controlRequest<AgentConfiguration>('/v1/chat/agents/' + encodeURIComponent(name) + '/config', {
    method: 'PUT', body: JSON.stringify({ document, expected_revision: revision }),
  });
}

export function getSessionConfiguration(sessionId: string) {
  return controlRequest<AgentConfiguration>('/v1/chat/sessions/' + encodeURIComponent(sessionId) + '/config');
}

export type LlmProtocol = "openai_responses" | "openai_chat" | "anthropic";
export interface LlmProvider { id: string; name: string; protocol: LlmProtocol; endpoint: string; models: string[]; revision: number; has_key: boolean }
export interface SaveLlmProvider { name: string; protocol: LlmProtocol; endpoint: string; models: string[]; api_key?: string; expected_revision: number | null }
export function listLlmProviders() { return controlRequest<LlmProvider[]>("/v1/chat/providers"); }
export function saveLlmProvider(id: string | null, input: SaveLlmProvider) {
  return controlRequest<LlmProvider>("/v1/chat/providers" + (id ? "/" + encodeURIComponent(id) : ""), { method: id ? "PUT" : "POST", body: JSON.stringify(input) });
}
export function generateProviderTemplate(id: string, runtime: "codex" | "opencode", model: string, document: string) {
  return controlRequest<{document:string}>("/v1/chat/providers/" + encodeURIComponent(id) + "/template", {method:"POST",body:JSON.stringify({runtime,model,document})});
}
